import { useParams } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { Dropdown } from '@quero-delivery/quero-components-web';
import { Modal } from '@querodelivery/uni-react';
import { FormToBlock } from '../form-to-block';
import type { MotoboyDetailsFormType } from '../form-to-block/types';
import { ConfirmModalBlock } from '../confirm-modal-block';
import { usePostBlockDeliveryman } from '@/_features/motoboys/api/post-block-deliveryman';
import { useGetBlocksDeliveryman } from '@/_features/motoboys/api/get-blocks-deliveryman';
import type { Block } from '@/_features/motoboys/api/types';
import { useDeleteBlockDeliveryman } from '@/_features/motoboys/api/delete-block-deliveryman';
import styles from './styles.module.scss';
import { MODAL_WIDTH } from '@/_constants/styles';

interface ModalBlockProps {
  open: boolean;
  onClose: () => void;
}

function ModalBlock({ open, onClose }: ModalBlockProps) {
  const { motoboyId } = useParams<{ motoboyId: string }>();
  const [params, setParams] = useState<MotoboyDetailsFormType | null>(null);
  const [paramsToDelete, setParamsToDelete] = useState<Block | null>(null);
  const [openModalConfirm, setOpenModalConfirm] = useState(false);

  const { mutate: mutateBlockDeliveryman, isLoading: isLoadingPostBlock } =
    usePostBlockDeliveryman({
      onSuccess: () => {
        setOpenModalConfirm(false);
        setParams(null);
      },
    });

  const { mutate: mutateDeleteeliveryman, isLoading: isLoadingDeleteBlock } =
    useDeleteBlockDeliveryman({
      onSuccess: () => {
        setOpenModalConfirm(false);
        setParamsToDelete(null);
      },
    });

  const { data: blocksData, isLoading } = useGetBlocksDeliveryman({
    deliverymanId: motoboyId || '',
  });

  const blockedOptions = useMemo(() => {
    if (blocksData) {
      const options = blocksData.data.map(block => ({
        key: block.blockId,
        text:
          block.placeName ||
          (block.isBlockedBySystem ? 'Bloqueio temporário' : 'Todos'),
        value: block.blockId,
      }));

      return options;
    }
    return [];
  }, [blocksData]);

  const valuesOptions = blockedOptions.map(item => item.value);

  function onRequest() {
    if (params && motoboyId) {
      mutateBlockDeliveryman({
        deliverymanId: motoboyId,
        hasBlockedAll: params.placeId === 'all',
        period:
          (params.periodType && params.period[params.periodType]) || undefined,
        blockReason: params.reasonToBlock,
        periodType: params.periodType || undefined,
        blockType: params.blockType,
        placeId: params.placeId !== 'all' ? params.placeId : undefined,
      });
    }
  }

  function onRequestDelete() {
    if (paramsToDelete && motoboyId) {
      mutateDeleteeliveryman({
        deliverymanId: motoboyId,
        blockId: paramsToDelete.blockId,
      });
    }
  }

  return openModalConfirm ? (
    <ConfirmModalBlock
      title={
        paramsToDelete
          ? 'Tem certeza que deseja remover o bloqueio deste estabelecimento?'
          : 'Tem certeza que deseja bloquear entregador?'
      }
      open={openModalConfirm}
      onClose={() => {
        setOpenModalConfirm(false);
        setParamsToDelete(null);
      }}
      onConfirm={paramsToDelete ? onRequestDelete : onRequest}
      params={params}
      paramsToDelete={paramsToDelete}
      isLoading={isLoadingPostBlock || isLoadingDeleteBlock}
    />
  ) : (
    <Modal
      isOpen={open}
      onClose={onClose}
      width={MODAL_WIDTH.md}
      header="Bloquear entregador"
    >
      {blockedOptions.length ? (
        <Dropdown
          className={styles.placeSelect}
          label="Estabelecimentos"
          placeholder="Estabelecimento"
          search
          options={blockedOptions}
          value={valuesOptions}
          onChange={(event, data) => {
            const target = event.target as HTMLElement;

            if (target.className.includes('delete')) {
              const selectedBlocked = blocksData?.data.find(
                block => !(data.value as string[]).includes(block.blockId),
              );

              if (selectedBlocked) {
                setParamsToDelete(selectedBlocked);
                setOpenModalConfirm(true);
              }
            }
          }}
          multiple
          loading={isLoading}
          disabled={isLoading}
          labelClassName={styles.spanSelect}
        />
      ) : null}
      <FormToBlock
        onCloseModal={onClose}
        setParams={setParams}
        params={params}
        setOpenModalConfirm={setOpenModalConfirm}
      />
    </Modal>
  );
}

export { ModalBlock };
